import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import axios from "axios";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import { SUPPORTED_LANGS } from "@/i18n";
import { HreflangTags } from "@/components/HreflangTags";
import { LocaleSEO } from "@/components/LocaleSEO";
import "@/App.css";

// Auth is cookie-based: send the httpOnly auth cookie with every same-origin
// /api request. No token is read from JS storage (XSS-safe).
axios.defaults.withCredentials = true;

import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import CalculatorPage from "@/pages/CalculatorPage";
import MethodologyPage from "@/pages/MethodologyPage";
import SavedEstimatesPage from "@/pages/SavedEstimatesPage";
import ScenariosPage from "@/pages/ScenariosPage";
import SharedScenarioComparisonPage from "@/pages/SharedScenarioComparisonPage";
import GuidancePage from "@/pages/GuidancePage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import SharedEstimatePage from "@/pages/SharedEstimatePage";
import AssessmentPage from "@/pages/AssessmentPage";
import MiniAssessmentPage from "@/pages/MiniAssessmentPage";
import ConsultantsPage from "@/pages/ConsultantsPage";
import AdminConsultantsPage from "@/pages/AdminConsultantsPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfUse from "@/pages/TermsOfUse";
import Disclaimer from "@/pages/Disclaimer";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import ProtectedRoute from "@/components/ProtectedRoute";

function LanguageSync() {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang && SUPPORTED_LANGS.includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  return null;
}

function LocalizedRoutes() {
  const location = useLocation();

  // CRITICAL: Check for session_id in URL fragment synchronously during render
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Redirect root to default language */}
      <Route path="/" element={<LangRedirect />} />

      {/* Language-prefixed routes */}
      <Route path="/:lang/*" element={<LangRoutes />} />

      {/* Non-prefixed shared routes (backwards compat with links already sent) */}
      <Route path="/shared/scenarios/:token" element={<SharedScenarioComparisonPage />} />
      <Route path="/shared/:token" element={<SharedEstimatePage />} />
    </Routes>
  );
}

function LangRedirect() {
  const { i18n } = useTranslation();
  const lang = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : 'en';
  return <Navigate to={`/${lang}`} replace />;
}

function LangRoutes() {
  const { lang } = useParams();

  // If the lang param isn't a valid language, redirect to /en
  if (!SUPPORTED_LANGS.includes(lang)) {
    return <Navigate to={`/en`} replace />;
  }

  return (
    <>
      <LanguageSync />
      <HreflangTags />
      <LocaleSEO />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/shared/scenarios/:token" element={<SharedScenarioComparisonPage />} />
        <Route path="/shared/:token" element={<SharedEstimatePage />} />
        <Route path="/assessment" element={<AssessmentPage />} />
        <Route path="/mini-assessment" element={<MiniAssessmentPage />} />
        <Route path="/consultants" element={<ConsultantsPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/guidance" element={
          <ProtectedRoute><GuidancePage /></ProtectedRoute>
        } />
        {/* The calculator is deliberately public: anonymous visitors get the full
            estimate, only saving/managing estimates requires an account. */}
        <Route path="/calculator" element={<CalculatorPage />} />
        <Route path="/methodology" element={<MethodologyPage />} />
        {/* Scenario analysis is public: comparing session scenarios needs no
            account, only saving/naming them does. */}
        <Route path="/scenarios" element={<ScenariosPage />} />
        <Route path="/estimates" element={
          <ProtectedRoute><SavedEstimatesPage /></ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute><AccountSettingsPage /></ProtectedRoute>
        } />
        <Route path="/admin/consultants" element={
          <ProtectedRoute><AdminConsultantsPage /></ProtectedRoute>
        } />
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <LocalizedRoutes />
          <Toaster position="bottom-right" theme="dark" />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
